import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "../../hooks/useCart";
import { API } from "../../config/constants";
import axios from "axios";

const CartDrawer = () => {
  const { cart, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, cartSubtotal, cartTax, cartShipping, cartTotal } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        items: cart,
        origin_url: window.location.origin,
        shipping: cartShipping,
        tax: cartTax,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      alert(`Checkout error: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => setIsCartOpen(false)}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-2xl font-bold text-[#0F2137]">Your Cart</h2>
                <button
                  onClick={() => setIsCartOpen(false)}
                  data-testid="close-cart"
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="mx-auto text-gray-300 mb-4" size={64} />
                    <p className="text-gray-500">Your cart is empty</p>
                    <Link
                      to="/merch"
                      onClick={() => setIsCartOpen(false)}
                      className="inline-block mt-4 text-[#D90429] font-medium hover:underline"
                    >
                      Browse Merch
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.product_id} className="flex gap-4 p-4 bg-gray-50 rounded-lg" data-testid={`cart-item-${item.product_id}`}>
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#0F2137]">{item.name}</h4>
                          <p className="text-[#D90429] font-bold">${item.price.toFixed(2)}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              className="p-1 hover:bg-gray-200 rounded"
                              data-testid={`decrease-qty-${item.product_id}`}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                              className="p-1 hover:bg-gray-200 rounded"
                              data-testid={`increase-qty-${item.product_id}`}
                            >
                              <Plus size={16} />
                            </button>
                            <button
                              onClick={() => removeFromCart(item.product_id)}
                              className="p-1 hover:bg-red-100 text-red-500 rounded ml-auto"
                              data-testid={`remove-item-${item.product_id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {cart.length > 0 && (
                <div className="p-6 border-t bg-gray-50">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600" data-testid="cart-subtotal-label">Subtotal</span>
                      <span className="text-sm font-medium" data-testid="cart-subtotal">${cartSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600" data-testid="cart-tax-label">Tax (7%)</span>
                      <span className="text-sm font-medium" data-testid="cart-tax">${cartTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600" data-testid="cart-shipping-label">Shipping</span>
                      <span className="text-sm font-medium" data-testid="cart-shipping">${cartShipping.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between items-center">
                      <span className="text-lg font-semibold" data-testid="cart-total-label">Total</span>
                      <span className="text-2xl font-bold text-[#D90429]" data-testid="cart-total">${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleCheckout}
                    disabled={isLoading}
                    data-testid="checkout-button"
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Processing..." : "Checkout"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
